import { useRef } from "react";
import { DateFilter } from "./internal/types";
import Flex from "../../../../components/Flex";
import {
  MaskedDateInput,
  MaskedDateInputRef,
} from "../../../../components/DateFieldSingle/internal/MaskedDateInput";
import { DateFieldSingleChange } from "../../../../components/DateFieldSingle";
import { useDateFieldSingleConversion } from "../../../../components/DateFieldSingle/internal/useDateFieldSingleConversion";
import { useDateFieldSingleState } from "../../../../components/DateFieldSingle/internal/useDateFieldSingleState";
import { DateFieldSingleCalendar } from "../../../../components/DateFieldSingle/internal/DateFieldSingleCalendar";

/**
 * Props for the FilterDateSingle component
 */
interface FilterDateSingleProps {
  /**
   * The date filter configuration object containing mode and other settings
   */
  filter: DateFilter;
  /**
   * The current draft value for the date filter in ISO 8601 format
   */
  draftValue: string | null;
  /**
   * Callback function to update the draft value when the date changes
   */
  onDraftChange: (value: string | null) => void;
}

/**
 * FilterDateSingle component for selecting a single date in toolbar filters.
 *
 * Features:
 * - Renders a masked date input field with format validation
 * - Provides calendar popup for date selection
 * - Integrates with toolbar filter state management
 * - Supports controlled and uncontrolled date selection
 * - Maintains draft state for filter changes
 */
export const FilterDateSingle = ({
  filter,
  draftValue,
  onDraftChange,
}: FilterDateSingleProps) => {
  const maskedDateInputRef = useRef<MaskedDateInputRef>(null);

  const handleChange = (change: DateFieldSingleChange) => {
    onDraftChange(change.date);
  };

  const { value, onChange } = useDateFieldSingleConversion({
    value: draftValue,
    onChange: handleChange,
  });

  const { handleInputChange, handleCalendarSelection } =
    useDateFieldSingleState({
      valueProp: value,
      onChange: (change) => onChange({ ...change, isDateValid: true }),
    });

  return (
    <Flex gap={4} direction="column">
      <MaskedDateInput
        mode={filter.mode}
        ref={maskedDateInputRef}
        onChange={handleInputChange}
        lastValidDate={value ?? null}
        autoComplete="off"
      />

      <DateFieldSingleCalendar
        value={value ?? null}
        onSelection={handleCalendarSelection}
      />
    </Flex>
  );
};
